import React, { useState, useEffect, useRef } from 'react';
import { 
  Typography, 
  Box, 
  Tabs, 
  Tab,
  styled,
  Collapse,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { api } from '../../../../services/api';
import { calculateDistance, calculatePriceByDistance } from '../../../../utils/distanceCalculator';
import { convertNumberToIndianWords } from '../../../../utils/numberToWords';

// Import our new components
import PriceBreakdown from './PriceBreakdown';
import DistancePriceComparison from './DistancePriceComparison';
import OutstationCalculator from './OutstationCalculator';
import LocalPricingDisplay from './LocalPricingDisplay';
import StandardPricingTable from './StandardPricingTable';
import KilometerDetails from './KilometerDetails';

interface PricingContainerProps {
  fullWidth?: boolean;
}

const PricingContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth'
})<PricingContainerProps>(({ theme, fullWidth }) => ({
  padding: theme.spacing(1),
  backgroundColor: '#1e1e1e',
  color: '#fff',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'calc(100vh - 120px)',
  overflow: 'hidden'
}));

const ServiceHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1),
  backgroundColor: '#333',
  marginBottom: theme.spacing(1),
  borderRadius: '4px',
}));

interface OfferInfo {
  id: number;
  name: string;
  discountAmount: number;
  discountPercent: number;
  validFrom: string;
  validTill: string;
  discountedPrice: number;
}

interface CarTypePricing {
  id?: number;
  carTypeId?: number;
  carType: string;
  makeCompany?: string;
  model?: string;
  capacity?: number;
  fuelType?: string;
  basePrice: number;
  pricePerKm?: number;
  pricePerDay?: number;
  minCharge?: number;
  negotiationPrice?: number;
  ratePerExtraKm?: number;
  driverAllowance?: number;
  ratePerKm?: number;
  tollCharges?: number;
  kmLimitPerDay?: number;
  hourLimitPerDay?: number;
  ratePerExtraHour?: number; // Add this field
  slot?: string;
  slotDescription?: string;
  offerInfo?: OfferInfo;
  offers?: OfferInfo[];
  pricingGroups?: { hourLimit: number, kmLimit: number, price: number }[];
}

interface Service {
  id: number;
  code: string;
  name: string;
  description: string;
  defaultSource: string;
  defaultDestination: string;
  defaultRequest: string;
  minHours?: string;
  minKm?: string;
  status: string;
  carTypePricing: CarTypePricing[];
}

interface PricingGroup {
  hourLimit: number;
  description: string;
  pricings: CarTypePricing[];
}

interface PricingData {
  services: Service[];
}

interface PricingTableProps {
  fullWidth?: boolean;
}

// Update interfaces to match new API response
interface NewCarOption {
  id: number;
  makeAndModel: string;
  fuelType: string;
  capacity: number;
  bags: number;
  carType: string;
  displayPrice: number;
  offerPrice: number;
  negotiationPrice: number;
  slot: string;
  kmLimitPerDay: number;
  ratePerExtraKm: number;
  ratePerExtraHour: number;
  basePricePerDay: number;
  basePricePerKm: number;
  driverAllowance: number;
  includedExpenses: string[];
  excludedExpenses: string[];
}

interface NewServiceData {
  serviceId: number;
  serviceName: string;
  serviceCode: string;
  description: string;
  defaultSource: string;
  defaultDestination: string;
  carOptions: NewCarOption[];
}

interface NewApiResponse {
  status: boolean;
  message: string;
  data: NewServiceData[];
}

const PricingTable: React.FC<PricingTableProps> = ({ fullWidth = false }) => {
  // Add ref to prevent duplicate calls
  const hasFetchedData = useRef(false);
  
  // Initialize with null, will be populated from API
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedServices, setExpandedServices] = useState<{[key: number]: boolean}>({});
  const [activeTab, setActiveTab] = useState<number>(0);
  const [activeLocalTab, setActiveLocalTab] = useState<number>(0);
  
  // State for date calculation
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // State for distance calculation
  const [sourceLocation, setSourceLocation] = useState<string>('');
  const [destinationLocation, setDestinationLocation] = useState<string>('');
  const [isRoundTrip, setIsRoundTrip] = useState<boolean>(true);
  const [returnCharges, setReturnCharges] = useState<string>('0');
  const [calculatingDistance, setCalculatingDistance] = useState<boolean>(false);
  const [calculatedDistance, setCalculatedDistance] = useState<number | null>(null);

  // State to store calculated prices without updating the table
  const [calculatedPrices, setCalculatedPrices] = useState<{[key: number]: number}>({});
  const [distanceBasedPrices, setDistanceBasedPrices] = useState<{
    [key: number]: {
      kmPrice: number;
      dayPrice: number;
      finalPrice: number;
      baseKmLimit: number;
      extraKm: number;
      baseKmPrice: number;
      extraKmPrice: number;
      returnCharges: number;
    }
  }>({});
  const [tripDays, setTripDays] = useState<number>(0);

  const fetchPricingData = async () => {
    // Prevent duplicate calls
    if (hasFetchedData.current) {
      return;
    }
    
    try {
      setLoading(true);
      hasFetchedData.current = true;
      
      const response = await api.getPricingTable();
      const apiResponse = response as NewApiResponse;
      
      if (!apiResponse.status || !apiResponse.data) {
        throw new Error(apiResponse.message || 'Invalid API response');
      }
      
      // Transform new API response to existing structure
      const transformedData: PricingData = {
        services: apiResponse.data.map((serviceData: NewServiceData) => {
          // Map service codes to match existing logic
          let mappedServiceCode = serviceData.serviceCode;
          if (serviceData.serviceCode === 'MUMBAI_AIRPORT') {
            mappedServiceCode = 'AIRPORT';
          } else if (serviceData.serviceCode === 'ONE_WAY') {
            mappedServiceCode = 'OUTSTATION';
          } else if (serviceData.serviceCode === 'OUTSTATION_ROUND') {
            mappedServiceCode = 'OUTSTATION_ROUND';
          } else if (serviceData.serviceCode === 'PUNE_LOCAL') {
            mappedServiceCode = 'LOCAL';
          }
          
          const service: Service = {
            id: serviceData.serviceId,
            code: mappedServiceCode,
            name: serviceData.serviceName,
            description: serviceData.description,
            defaultSource: serviceData.defaultSource,
            defaultDestination: serviceData.defaultDestination,
            defaultRequest: '', // Not provided in new API
            status: 'ACTIVE', // Assuming active
            carTypePricing: serviceData.carOptions.map((carOption: NewCarOption) => {
              // Extract make and model
              const [makeCompany, ...modelParts] = carOption.makeAndModel.split(' ');
              const model = modelParts.join(' ');
              
              // Map slot to hour limit
              let hourLimitPerDay = 0;
              if (carOption.slot === '6_HOURS') hourLimitPerDay = 6;
              else if (carOption.slot === '9_HOURS') hourLimitPerDay = 9;
              else if (carOption.slot === '12_HOURS') hourLimitPerDay = 12;
              
              const carTypePricing: CarTypePricing = {
                id: carOption.id,
                carTypeId: carOption.id,
                carType: carOption.carType,
                makeCompany: makeCompany,
                model: model,
                capacity: carOption.capacity,
                fuelType: carOption.fuelType,
                basePrice: carOption.displayPrice,
                pricePerKm: carOption.basePricePerKm,
                pricePerDay: carOption.basePricePerDay,
                negotiationPrice: carOption.negotiationPrice,
                ratePerExtraKm: carOption.ratePerExtraKm,
                ratePerExtraHour: carOption.ratePerExtraHour, // Map this field
                driverAllowance: carOption.driverAllowance,
                ratePerKm: carOption.basePricePerKm,
                kmLimitPerDay: carOption.kmLimitPerDay,
                hourLimitPerDay: hourLimitPerDay,
                slot: carOption.slot,
                slotDescription: carOption.slot.replace('_', ' ').toLowerCase(),
                // Create offer info from the pricing data
                offerInfo: carOption.offerPrice !== carOption.displayPrice ? {
                  id: carOption.id,
                  name: 'Special Offer',
                  discountAmount: carOption.displayPrice - carOption.offerPrice,
                  discountPercent: Math.round(((carOption.displayPrice - carOption.offerPrice) / carOption.displayPrice) * 100),
                  validFrom: new Date().toISOString(),
                  validTill: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
                  discountedPrice: carOption.offerPrice
                } : undefined
              };
              
              return carTypePricing;
            })
          };
          
          return service;
        })
      };
      
      setPricingData(transformedData);
      
      // Initialize expanded state for all services
      const expandedState: {[key: number]: boolean} = {};
      transformedData.services.forEach((service: Service) => {
        expandedState[service.id] = false;
      });
      setExpandedServices(expandedState);
      
    } catch (err) {
      console.error('Error fetching pricing data:', err);
      setError('Failed to load pricing data. Please try again later.');
      hasFetchedData.current = false; // Reset on error to allow retry
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricingData();
  }, []); // Empty dependency array to run only once

  // No longer need accordion functionality

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleLocalTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveLocalTab(newValue);
  };

  // Calculate price based on days
  const calculateOutstationPrice = (service: Service, days: number) => {
    if (!service || !service.carTypePricing || days <= 0) {
      return;
    }
    
    setTripDays(days);
    
    // Calculate prices for each car type
    const prices: {[key: number]: number} = {};
    
    service.carTypePricing.forEach(pricing => {
      // Calculate base price = base price per day * number of days
      const basePrice = pricing.basePrice * days;
      
      // Add driver allowance if available
      const driverAllowance = (pricing.driverAllowance || 0) * days;
      
      // Total price
      const totalPrice = basePrice + driverAllowance;
      
      // Store the calculated price
      if (pricing.carTypeId !== undefined) {
        prices[pricing.carTypeId] = totalPrice;
      } else if (pricing.id !== undefined) {
        // Use id as fallback if carTypeId is not available
        prices[pricing.id] = totalPrice;
      }
    });
    
    setCalculatedPrices(prices);
  };

  // Calculate price based on distance between source and destination
  const calculateDistanceBasedPrice = (service: Service, distance: number, isRoundTrip: boolean, returnCharges: string) => {
    // Calculate distance-based price with return charges
    if (!service || !service.carTypePricing || distance <= 0) {
      return;
    }
    
    setCalculatedDistance(distance);
    
    // Calculate prices for each car type
    const prices: {[key: number]: {
      kmPrice: number, 
      dayPrice: number, 
      finalPrice: number, 
      baseKmLimit: number, 
      extraKm: number, 
      baseKmPrice: number, 
      extraKmPrice: number,
      returnCharges: number
    }} = {};
    
    service.carTypePricing.forEach(pricing => {
      // Get the km limit per day if available
      // Default to 300 KM per day if not specified
      const kmLimit = pricing.kmLimitPerDay || 300;
      const totalKmLimit = kmLimit * tripDays;
      
      // Calculate extra km if applicable
      let extraKm = 0;
      let baseKmPrice = 0;
      let extraKmPrice = 0;
      
      if (distance > totalKmLimit && totalKmLimit > 0) {
        // If distance exceeds the limit, calculate extra km charges
        extraKm = distance - totalKmLimit;
        baseKmPrice = calculatePriceByDistance(totalKmLimit, pricing.ratePerKm || 0);
        extraKmPrice = extraKm * (pricing.ratePerExtraKm || pricing.ratePerKm || 0);
      } else {
        // If within limit or no limit specified, calculate normally
        baseKmPrice = calculatePriceByDistance(distance, pricing.ratePerKm || 0);
      }
      
      // Add driver allowance
      const driverAllowance = (pricing.driverAllowance || 0) * tripDays;
      
      // Only include return charges when Round Trip is false
      const returnCharge = !isRoundTrip ? (parseFloat(returnCharges) || 0) : 0;
      // Return charges are only applied when it's not a round trip
      
      // Total price based on distance
      const totalDistancePrice = baseKmPrice + extraKmPrice + driverAllowance;
      
      // Calculate day-based price for comparison
      const dayPrice = (pricing.basePrice * tripDays) + driverAllowance;
      
      // Choose the lower price as the final price
      // We exclude returnCharge from this comparison and add it separately
      const basePrice = Math.min(baseKmPrice + extraKmPrice + driverAllowance, dayPrice);
      const finalPrice = basePrice + returnCharge;
      
      // Store the calculated prices with extra km information
      if (pricing.carTypeId !== undefined) {
        // Store the final price with return charges included
        
        prices[pricing.carTypeId] = {
          kmPrice: totalDistancePrice,
          dayPrice: dayPrice,
          finalPrice: finalPrice,
          baseKmLimit: totalKmLimit,
          extraKm: extraKm,
          baseKmPrice: baseKmPrice,
          extraKmPrice: extraKmPrice,
          returnCharges: returnCharge
        };
      } else if (pricing.id !== undefined) {
        // Use id as fallback if carTypeId is not available
        // Store the final price with return charges included
        
        prices[pricing.id] = {
          kmPrice: totalDistancePrice,
          dayPrice: dayPrice,
          finalPrice: finalPrice,
          baseKmLimit: totalKmLimit,
          extraKm: extraKm,
          baseKmPrice: baseKmPrice,
          extraKmPrice: extraKmPrice,
          returnCharges: returnCharge
        };
      }
    });
    
    setDistanceBasedPrices(prices);
  };

  // Group Pune Local pricing by hourLimitPerDay
  const groupLocalPricing = (service: Service): PricingGroup[] => {
    if (!service || !service.carTypePricing) return [];
    
    const groupedPricing: {[key: number]: CarTypePricing[]} = {};
    
    service.carTypePricing.forEach(pricing => {
      const hourLimit = pricing.hourLimitPerDay || 0;
      if (!groupedPricing[hourLimit]) {
        groupedPricing[hourLimit] = [];
      }
      groupedPricing[hourLimit].push(pricing);
    });
    
    return Object.keys(groupedPricing).map(hourLimit => {
      const numHourLimit = parseInt(hourLimit, 10);
      return {
        hourLimit: numHourLimit,
        description: `${numHourLimit} Hour Package`,
        pricings: groupedPricing[numHourLimit]
      };
    }).sort((a, b) => a.hourLimit - b.hourLimit);
  };

  if (loading) {
    return (
      <PricingContainer>
        <Typography variant="h6">Loading pricing data...</Typography>
      </PricingContainer>
    );
  }

  if (error) {
    return (
      <PricingContainer>
        <Typography variant="h6" color="error">{error}</Typography>
      </PricingContainer>
    );
  }

  if (!pricingData || !pricingData.services || pricingData.services.length === 0) {
    return (
      <PricingContainer>
        <Typography variant="h6">No pricing data available.</Typography>
      </PricingContainer>
    );
  }

  return (
    <PricingContainer fullWidth={fullWidth}>
      {error && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(255,0,0,0.1)', borderRadius: 1 }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      )}

      {pricingData && pricingData.services && pricingData.services.length > 0 ? (
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            flexShrink: 0,
            '& .MuiTabs-indicator': {
              backgroundColor: '#4fc3f7',
            },
            '& .MuiTab-root': {
              color: '#e0e0e0',
              '&.Mui-selected': {
                color: '#4fc3f7',
              },
            },
          }}
        >
          {pricingData.services.map((service) => (
            <Tab key={service.id} label={service.name} />
          ))}
        </Tabs>
      ) : (
        <Typography variant="subtitle1" sx={{ p: 2, color: '#e0e0e0' }}>
          No pricing data available. Please check the API connection.
        </Typography>
      )}
      
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        overflowX: 'hidden',
        pr: 1,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255,255,255,0.3)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(255,255,255,0.5)',
          },
        },
      }}>
        {pricingData && pricingData.services && pricingData.services.map((service, index) => (
          <Box key={service.id} sx={{ display: activeTab === index ? 'block' : 'none' }}>
            <ServiceHeader>
              <Typography variant="subtitle1">{service.name}</Typography>
            </ServiceHeader>
            
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                <strong>Source:</strong> {service.defaultSource}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                <strong>Destination:</strong> {service.defaultDestination}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, fontSize: '0.75rem' }}>
                <strong>Description:</strong> {service.description}
              </Typography>
            </Box>
            
            {service.code === 'LOCAL' ? (
              <>
                {/* Inner tabs for different hour limits */}
                {(() => {
                  const groups = groupLocalPricing(service);
                  if (groups.length > 0) {
                    return (
                      <>
                        <Tabs 
                          value={activeLocalTab} 
                          onChange={handleLocalTabChange}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{ 
                            mb: 1,
                            flexShrink: 0,
                            '& .MuiTab-root': { 
                              color: '#ccc',
                              fontSize: '0.7rem',
                              minWidth: 'auto',
                              padding: '6px 12px'
                            },
                            '& .Mui-selected': { color: '#fff' }
                          }}
                        >
                          {groups.map((group, idx) => (
                            <Tab key={idx} label={`${group.hourLimit}h / ${group.pricings[0].kmLimitPerDay}km`} />
                          ))}
                        </Tabs>
                        
                        {groups.map((group, idx) => (
                          <Box key={idx} sx={{ display: activeLocalTab === idx ? 'block' : 'none' }}>
                            <LocalPricingDisplay group={group} />
                          </Box>
                        ))}
                      </>
                    );
                  }
                  return null;
                })()}
              </>
            ) : (
              <>
                {/* Standard pricing table for non-local services */}
                <StandardPricingTable service={service} />
                
                {service.id === 3 && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    backgroundColor: '#1e1e1e', 
                    borderRadius: 1,
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}>
                    {/* <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#4fc3f7', fontSize: '0.85rem' }}>
                      Outstation Trip Price Calculator
                    </Typography> */}
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Unified calculator for date and distance */}
                        {/* <OutstationCalculator 
                          service={service}
                          onCalculatePrice={(days) => {
                            calculateOutstationPrice(service, days);
                          }}
                          onCalculateDistancePrice={(distance, isRoundTrip, returnCharges) => {
                            calculateDistanceBasedPrice(service, distance, isRoundTrip, returnCharges);
                          }}
                        /> */}
                      </Box>
                      
                      {/* Show unified price breakdown */}
                      {(Object.keys(calculatedPrices).length > 0 || (calculatedDistance !== null && Object.keys(distanceBasedPrices).length > 0)) && (
                        <Box sx={{ 
                          backgroundColor: '#1e1e1e', 
                          p: 2, 
                          borderRadius: 2, 
                          mt: 1.5, 
                          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                          maxWidth: '100%',
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.5, pb: 1, borderBottom: '1px solid #333' }}>
                            {/* Title row with distance and days */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                              {calculatedDistance !== null && (
                                <Box sx={{ 
                                  backgroundColor: '#4caf50', 
                                  color: '#000', 
                                  fontWeight: 'bold', 
                                  px: 1.5, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  fontSize: '0.75rem'
                                }}>
                                  {calculatedDistance.toFixed(0)} KM
                                </Box>
                              )}
                              <Box sx={{ 
                                backgroundColor: '#64b5f6', 
                                color: '#000', 
                                fontWeight: 'bold', 
                                px: 1.5, 
                                py: 0.5, 
                                borderRadius: 1,
                                fontSize: '0.75rem'
                              }}>
                                {tripDays} DAY{tripDays > 1 ? 'S' : ''}
                              </Box>
                              {/* Display Daily Limit in Header - Always show with a fallback value */}
                              <Box sx={{ 
                                backgroundColor: 'rgba(255,167,38,0.2)', 
                                color: '#ffa726', 
                                fontWeight: 'bold', 
                                px: 1.5, 
                                py: 0.5, 
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                <span style={{ marginRight: '4px' }}>Daily Limit:</span>
                                {(() => {
                                  // Get the first car type's km limit as reference
                                  if (Object.keys(distanceBasedPrices).length > 0) {
                                    const firstCarTypeId = Object.keys(distanceBasedPrices)[0];
                                    // Use type assertion to handle the index access safely
                                    const firstCarDetails = distanceBasedPrices[firstCarTypeId as any];
                                    return firstCarDetails ? Math.round(firstCarDetails.baseKmLimit / tripDays) : 300;
                                  }
                                  return 300; // Default value
                                })()}
                                <span style={{ marginLeft: '2px' }}>KM</span>
                              </Box>
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.9rem' }}>
                              Trip Price Breakdown
                            </Typography>
                          </Box>
                          
                          {/* Kilometer Details section at top level */}
                          <KilometerDetails
                            tripDays={tripDays}
                            distanceDetails={Object.keys(distanceBasedPrices).length > 0 ? distanceBasedPrices[Object.keys(distanceBasedPrices)[0] as any] : undefined}
                            calculatedDistance={calculatedDistance !== null ? calculatedDistance : undefined}
                          />
                          
                          <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                            {service.carTypePricing.map((pricing) => {
                              // Get the distance-based price data for this car type if available
                              const distanceDetails = calculatedDistance !== null ? 
                                (pricing.carTypeId !== undefined ? distanceBasedPrices[pricing.carTypeId] : 
                                pricing.id !== undefined ? distanceBasedPrices[pricing.id] : undefined) : undefined;
                              
                              // Get the date-based price if available
                              const dateBasedPrice = Object.keys(calculatedPrices).length > 0 ? 
                                (pricing.carTypeId !== undefined ? calculatedPrices[pricing.carTypeId] : 
                                pricing.id !== undefined ? calculatedPrices[pricing.id] : 0) : undefined;
                              
                              // Skip if no price data is available
                              if (!distanceDetails && !dateBasedPrice) return null;
                              
                              return (
                                <PriceBreakdown
                                  key={pricing.carTypeId || pricing.id || index}
                                  pricing={pricing}
                                  tripDays={tripDays}
                                  calculatedPrice={dateBasedPrice || (distanceDetails ? distanceDetails.finalPrice : 0)}
                                  negotiationPrice={pricing.negotiationPrice}
                                  offerInfo={pricing.offerInfo}
                                  distanceDetails={distanceDetails}
                                  calculatedDistance={calculatedDistance || undefined}
                                  isRoundTrip={isRoundTrip}
                                  returnCharges={returnCharges ? parseFloat(returnCharges) : 0}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                      
                      {/* Show price breakdown for combined calculation */}
                      {calculatedDistance !== null && startDate && endDate && Object.keys(calculatedPrices).length > 0 && (
                        <Box sx={{ 
                          backgroundColor: '#1e1e1e', 
                          p: 2, 
                          borderRadius: 2, 
                          mt: 1.5, 
                          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                          maxWidth: '100%',
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: '1px solid #333' }}>
                            <Box sx={{ 
                              backgroundColor: '#ffeb3b', 
                              color: '#000', 
                              fontWeight: 'bold', 
                              px: 1.5, 
                              py: 0.5, 
                              borderRadius: 1,
                              mr: 1.5,
                              fontSize: '0.9rem'
                            }}>
                              COMBINED
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#fff' }}>
                              Combined Price Recommendation
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" sx={{ mb: 2, color: '#e0e0e0' }}>
                            Based on both date and distance calculations, here are the recommended prices for your trip.
                          </Typography>
                          
                          <Box>
                            {service.carTypePricing.map((pricing) => {
                              // Get the distance-based price data for this car type
                              const distancePrice = pricing.carTypeId !== undefined ? distanceBasedPrices[pricing.carTypeId] : pricing.id !== undefined ? distanceBasedPrices[pricing.id] : undefined;
                              if (!distancePrice) return null;
                              
                              // Get the date-based price
                              const datePrice = pricing.carTypeId !== undefined ? calculatedPrices[pricing.carTypeId] : pricing.id !== undefined ? calculatedPrices[pricing.id] : 0;
                              
                              // Choose the lower price (handle possible undefined values)
                              const finalPrice = distancePrice ? Math.min(distancePrice.finalPrice, datePrice) : datePrice;
                              const isByDistanceCheaper = distancePrice ? distancePrice.finalPrice < datePrice : false;
                              
                              return (
                                <Box key={pricing.carTypeId} sx={{ mb: 3, pb: 2, borderRadius: 2, overflow: 'hidden', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, backgroundColor: 'rgba(0,0,0,0.2)', p: 1.5, borderTopLeftRadius: 2, borderTopRightRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 'bold',
                                          color: (() => {
                                            // Assign different colors based on car type
                                            const carType = pricing.carType.toLowerCase();
                                            if (carType.includes('sedan')) return '#42a5f5'; // Blue for Sedan
                                            if (carType.includes('suv')) return '#ffb74d'; // Orange for SUV
                                            if (carType.includes('premium')) return '#4caf50'; // Green for Premium
                                            if (carType.includes('luxury')) return '#ffd54f'; // Yellow for Luxury
                                            if (carType.includes('hatchback')) return '#4db6ac'; // Teal for Hatchback
                                            return '#e0e0e0'; // Default light gray
                                          })(),
                                          backgroundColor: (() => {
                                            // Assign different background colors based on car type
                                            const carType = pricing.carType.toLowerCase();
                                            if (carType.includes('sedan')) return 'rgba(66, 165, 245, 0.15)'; // Light blue bg
                                            if (carType.includes('suv')) return 'rgba(255, 183, 77, 0.15)'; // Light orange bg
                                            if (carType.includes('premium')) return 'rgba(76, 175, 80, 0.15)'; // Light green bg
                                            if (carType.includes('luxury')) return 'rgba(255, 213, 79, 0.15)'; // Light yellow bg
                                            if (carType.includes('hatchback')) return 'rgba(77, 182, 172, 0.15)'; // Light teal bg
                                            return 'transparent'; // Default transparent
                                          })(),
                                          px: 1,
                                          py: 0.5,
                                          borderRadius: 1,
                                          display: 'inline-block'
                                        }}
                                      >
                                        {pricing.carType}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                                        - {pricing.makeCompany} {pricing.model}
                                      </Typography>
                                    </Box>
                                    
                                    <Box sx={{
                                      backgroundColor: isByDistanceCheaper ? 'rgba(76,175,80,0.15)' : 'rgba(100,181,246,0.15)',
                                      color: isByDistanceCheaper ? '#4caf50' : '#64b5f6',
                                      fontWeight: 'bold',
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: '0.8rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      {isByDistanceCheaper ? 'Distance Based' : 'Date Based'}
                                    </Box>
                                  </Box>
                                  
                                  <Box sx={{ px: 1.5, py: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e0e0e0' }}>
                                        Final Recommended Price:
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#ffd54f' }}>
                                        ₹{finalPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                      </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px dashed #444' }}>
                                      <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                                        Based on {isByDistanceCheaper ? `distance (${calculatedDistance.toFixed(0)} km)` : `duration (${tripDays} day${tripDays > 1 ? 's' : ''})`}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#4caf50' }}>
                                        Save ₹{Math.abs(distancePrice.finalPrice - datePrice).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        ))}
      </Box>
    </PricingContainer>
  );
};

export default PricingTable;
